import { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate } from "@remix-run/react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useCombobox } from "downshift";
import debounce from "lodash-es/debounce";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

import {
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  List,
  ListItem,
  InputLeftElement,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  useDisclosure,
  Box,
  IconButton,
  Text,
  Tooltip,
  VisuallyHidden,
  useColorModeValue,
  HStack,
} from "@chakra-ui/react";

// Copy for the "Not currently monitored" affordance on Aspira-cohort
// (currently AlbertaParks only) search results. Single source of truth so the
// visible line, tooltip, aria-label, and screen-reader announcement all read
// the same string — see the Alberta Parks ongoing-comms design doc for the
// vocabulary lock ("Paused" is reserved for user-paused request state; "Not
// currently monitored" is the resolved phrase).
const UNAVAILABLE_STATUS_LINE = "Not currently monitored";
const UNAVAILABLE_TOOLTIP_LABEL =
  "Schnerp stopped monitoring Alberta Parks in August 2025. Click for the full story.";

import { SearchIcon, TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

import { GoLiveDate } from "#app/components/experimental/GoLiveDate.jsx";
import { useOptionalUser } from "#app/hooks/useUser.js";
import { FacilityRequestModal } from "#app/components/FacilityRequestModal.jsx";
import { COUNTRIES, COUNTRY_CODES } from "#app/config/countries.js";
import { capturePostHog } from "#app/utils/posthog.client.js";

function findMatchingAlias(searchMeta, query) {
  if (!query || !searchMeta) return null;
  try {
    const aliases = Array.isArray(searchMeta)
      ? searchMeta
      : JSON.parse(searchMeta);
    const q = query.toLowerCase().trim();
    return q ? aliases.find((a) => a.text.includes(q)) : null;
  } catch {
    return null;
  }
}

export function FacilitySearchCombobox({
  label = "Search for a campground, caravan park, hut, backcountry site, or shuttle",
  placeholder:
    defaultPlaceholder = "e.g. Lake O'Hara, Milford Track, Osprey Bay...",
  helperText:
    defaultHelperText = "We watch parks in Canada, the United States, Australia, and New Zealand",
  country: initialCountry,
  agencyIds,
  facilityIds,
  showCountryPills = false,
  // Where the country filter pills sit relative to the input. The homepage hero
  // uses "bottom" (search box leads, filters follow); the /new form keeps the
  // default "top".
  pillsPlacement = "top",
  // When false, the helper text under the search is hidden (the home hero leads
  // with country pills instead; country pages show their per-country copy).
  showHelper = true,
  // Alignment for the label / pills / helper text. Defaults to "start" when a
  // visible label is present, "center" otherwise. The homepage hero forces
  // "center"; the /new form leaves it as the default (left).
  align,
  // Font weight for the visible label. Defaults to "semibold" (homepage hero);
  // the /new form passes "normal" for a lighter form-field label.
  labelFontWeight = "semibold",
  ...rest
}) {
  const bg = useColorModeValue("white", "gray.900");
  const highlightBg = useColorModeValue("brand.500", "brand.200");
  const highlightColor = useColorModeValue("white", "black");
  const border = useColorModeValue("gray.300", "gray.800");

  const navigate = useNavigate();
  const user = useOptionalUser();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const [selectedCountry, setSelectedCountry] = useState(
    initialCountry || null,
  );
  const [currValue, setCurrValue] = useState({ inputValue: "" });
  const debounceSetCurrValue = useRef(null);
  if (!debounceSetCurrValue.current) {
    debounceSetCurrValue.current = debounce(setCurrValue, 200);
  }

  // TanStack Query instead of a Remix fetcher: a failed suggestion fetch must
  // never bubble to the route ErrorBoundary — WebKit kills in-flight fetches
  // on page dismissal/backgrounding ("TypeError: Load failed"), which made
  // this the app's noisiest Sentry issue. useQuery contains the error and
  // retries transient failures; on failure the dropdown just stays empty.
  const searchParams = new URLSearchParams({
    q: currValue.inputValue,
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || "",
  });
  if (selectedCountry) searchParams.set("country", selectedCountry);
  if (agencyIds) searchParams.set("agencyIds", agencyIds.join(","));
  if (facilityIds) searchParams.set("facilityIds", facilityIds.join(","));

  const {
    data,
    isFetching: isLoading,
    isError,
  } = useQuery({
    queryKey: ["FacilitySearch", searchParams.toString()],
    queryFn: async () => {
      const res = await fetch(`/api/facilities?${searchParams}`);
      if (!res.ok) throw new Error(`Facility search failed: ${res.status}`);
      return res.json();
    },
    placeholderData: keepPreviousData,
  });

  const items = data?.length ? data : [];
  const queryRef = useRef("");
  queryRef.current = currValue.inputValue;

  // Dynamic placeholder and helper text based on selected country
  const placeholder = selectedCountry
    ? COUNTRIES[selectedCountry]?.placeholder || defaultPlaceholder
    : defaultPlaceholder;
  const helperText = selectedCountry
    ? COUNTRIES[selectedCountry]?.helperText || defaultHelperText
    : defaultHelperText;
  const contentAlign = align ?? (label ? "start" : "center");
  const pillsOnTop = showCountryPills && pillsPlacement === "top";
  const pillsOnBottom = showCountryPills && pillsPlacement === "bottom";

  const countryPills = showCountryPills ? (
    <>
      <HStack
        spacing={2}
        // `safe center` keeps centering when the pills fit (desktop) but falls
        // back to left-aligned when they overflow (mobile) — plain `center`
        // would clip the first pill off the left edge and make it unscrollable.
        justify={contentAlign === "center" ? "safe center" : contentAlign}
        overflowX="auto"
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
        role="radiogroup"
        aria-label="Filter by country"
      >
        <Button
          size="xs"
          borderRadius="full"
          variant={selectedCountry === null ? "primary" : "outline"}
          role="radio"
          aria-checked={selectedCountry === null}
          onClick={() => setSelectedCountry(null)}
          minH="32px"
          px={3}
          flexShrink={0}
        >
          🌎 All
        </Button>
        {COUNTRY_CODES.map((code) => (
          <Button
            key={code}
            size="xs"
            borderRadius="full"
            variant={selectedCountry === code ? "primary" : "outline"}
            role="radio"
            aria-checked={selectedCountry === code}
            onClick={() => setSelectedCountry(code)}
            minH="32px"
            px={3}
            flexShrink={0}
            leftIcon={
              COUNTRIES[code].flag ? (
                <Box as="span" aria-hidden="true">
                  {COUNTRIES[code].flag}
                </Box>
              ) : undefined
            }
          >
            {COUNTRIES[code].label}
          </Button>
        ))}
      </HStack>
    </>
  ) : null;

  // Stage 2 of the funnel: searching for a campground. This is anonymous,
  // pre-signup intent the DB can't see (it only logs misses via
  // FacilitySearchMiss). Fire once per settled, non-trivial query with its
  // result count so search -> request conversion and demand are measurable.
  const lastSearchRef = useRef("");
  useEffect(() => {
    const q = currValue.inputValue?.trim();
    if (!q || q.length < 3 || isLoading || isError) return;
    if (lastSearchRef.current === q) return;
    lastSearchRef.current = q;
    const results = Array.isArray(data) ? data : [];
    capturePostHog("campground_searched", {
      query: q,
      result_count: results.length,
      had_results: results.length > 0,
      country: selectedCountry || null,
    });
  }, [currValue.inputValue, isLoading, isError, data, selectedCountry]);

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getToggleButtonProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    id: "FacilitySearchCombobox",
    items,
    itemToString: (item) => item.name,
    onSelectedItemChange({ selectedItem }) {
      if (!selectedItem) return;

      // Unavailable facilities (currently Alberta Parks only — see
      // app/routes/api/facilities.jsx) route to the canonical explainer page
      // with the original query string preserved. The /alberta-parks page
      // renders correctly with `q` missing, so URL-encoding an empty string
      // is safe.
      if (selectedItem.status === "unavailable") {
        const q = queryRef.current || "";
        navigate(`/alberta-parks?from=search&q=${encodeURIComponent(q)}`);
        return;
      }

      let target = `/new/${selectedItem.slug}`;

      const queryLower = queryRef.current?.toLowerCase().trim() || "";
      const nameMatches = selectedItem.name.toLowerCase().includes(queryLower);
      const subNameMatches = selectedItem.subName
        .toLowerCase()
        .includes(queryLower);

      const alias =
        !nameMatches && !subNameMatches
          ? findMatchingAlias(selectedItem.searchMeta, queryRef.current)
          : null;
      if (alias) {
        const params = new URLSearchParams({
          aliasType: alias.type,
          aliasLabel: alias.label,
        });
        target += `?${params}`;
      }

      navigate(target);
    },
    onInputValueChange: debounceSetCurrValue.current,
  });

  const memoizedItems = useMemo(() => {
    if (!items || items.length === 0) {
      return [];
    }

    return items.map((item) => {
      const nameSegments = parse(
        item.name,
        match(item.name, currValue.inputValue, {
          insideWords: true,
        }),
      );
      const subNameSegments = parse(
        item.subName,
        match(item.subName, currValue.inputValue, {
          insideWords: true,
        }),
      );

      const queryLower = currValue.inputValue.toLowerCase().trim();
      const nameMatches = item.name.toLowerCase().includes(queryLower);
      const subNameMatches = item.subName.toLowerCase().includes(queryLower);

      const matchedAlias =
        !nameMatches && !subNameMatches
          ? findMatchingAlias(item.searchMeta, currValue.inputValue)
          : null;

      const aliasSegments = matchedAlias
        ? parse(
            matchedAlias.label,
            match(matchedAlias.label, currValue.inputValue, {
              insideWords: true,
            }),
          )
        : null;

      return {
        item,
        nameSegments,
        subNameSegments,
        matchedAlias,
        aliasSegments,
      };
    });
  }, [items, currValue]);

  return (
    <Box pos="relative" textAlign={contentAlign} {...rest}>
      <FormControl>
        {label ? (
          <FormLabel
            {...getLabelProps()}
            textAlign="inherit"
            fontWeight={labelFontWeight}
            fontSize="md"
          >
            {label}
          </FormLabel>
        ) : (
          <VisuallyHidden>
            <FormLabel {...getLabelProps()} textAlign="inherit">
              {label}
            </FormLabel>
          </VisuallyHidden>
        )}
        {pillsOnTop && <Box mb={3}>{countryPills}</Box>}
        <InputGroup size="lg" mt={pillsOnTop ? 0 : 4}>
          <InputLeftElement
            pointerEvents="none"
            children={isLoading ? <Spinner size="sm" /> : <SearchIcon />}
          />
          <Input
            {...getInputProps()}
            autoComplete="off"
            placeholder={placeholder}
            pr="6rem"
            data-testid="facility-search-input"
          />
          <InputRightElement
            width="3rem"
            children={
              <>
                <IconButton
                  size="sm"
                  {...getToggleButtonProps()}
                  icon={isOpen ? <TriangleUpIcon /> : <TriangleDownIcon />}
                />
              </>
            }
          />
        </InputGroup>
        <List
          {...getMenuProps()}
          textAlign="start"
          w="100%"
          position="absolute"
          zIndex="dropdown"
          overflowY="scroll"
          maxH="300px"
          mt="1px"
          bg={bg}
          borderBottomWidth={isOpen ? "1px" : "0px"}
          borderLeftWidth={isOpen ? "1px" : "0px"}
          borderRightWidth={isOpen ? "1px" : "0px"}
          borderRadius="md"
          borderColor={border}
        >
          {!isOpen ? null : memoizedItems.length === 0 ? (
            <ListItem px={4} py={2} display="flex" alignItems="center" gap={2}>
              <Text fontSize={{ base: "sm", md: "md" }} color="muted">
                No results. Try a different search, or
              </Text>
              {currValue.inputValue && (
                <Button
                  size="sm"
                  colorScheme="brand"
                  flexShrink={0}
                  onClick={(e) => {
                    e.preventDefault();
                    onModalOpen();
                  }}
                >
                  Suggest it
                </Button>
              )}
            </ListItem>
          ) : (
            memoizedItems.map(
              (
                {
                  item,
                  nameSegments,
                  subNameSegments,
                  matchedAlias,
                  aliasSegments,
                },
                index,
              ) => {
                const isActive = index === highlightedIndex;
                const isUnavailable = item.status === "unavailable";
                const aliasTypeLabel =
                  {
                    route: "route",
                    site: "campsite",
                    location: "location",
                    park: "park",
                  }[matchedAlias?.type] ?? null;

                // A campground is in its park, near a location, and includes
                // its sites and routes.
                const aliasPrefix =
                  {
                    location: "↳ near",
                    park: "↳ in",
                  }[matchedAlias?.type] ?? "↳ includes";

                const listItem = (
                  <ListItem
                    {...getItemProps({ item, index })}
                    key={item.id}
                    transition="background-color 220ms, color 220ms"
                    bg={isActive ? highlightBg : null}
                    color={isActive ? highlightColor : null}
                    px={4}
                    py={2}
                    cursor="pointer"
                    data-testid="facility-search-item"
                    aria-label={
                      isUnavailable
                        ? `${item.name}, ${UNAVAILABLE_STATUS_LINE.toLowerCase()}`
                        : undefined
                    }
                  >
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={isActive ? "bg-muted" : "muted"}
                    >
                      {subNameSegments.map(({ text, highlight }, index) => {
                        return (
                          <Text
                            key={index}
                            as="span"
                            fontWeight={highlight ? "bold" : "normal"}
                          >
                            {text}
                          </Text>
                        );
                      })}
                    </Text>
                    <Text fontSize={{ base: "md", md: "lg" }}>
                      {nameSegments.map(({ text, highlight }, index) => {
                        return (
                          <Text
                            key={index}
                            as="span"
                            fontWeight={highlight ? "bold" : "normal"}
                          >
                            {text}
                          </Text>
                        );
                      })}
                      {isUnavailable && (
                        <VisuallyHidden>
                          , {UNAVAILABLE_STATUS_LINE.toLowerCase()}. Schnerp
                          stopped monitoring Alberta Parks in August 2025.
                        </VisuallyHidden>
                      )}
                    </Text>
                    {isUnavailable && (
                      <Text
                        fontSize="sm"
                        fontStyle="italic"
                        color={isActive ? "bg-emphasized" : "muted"}
                        mt={0.5}
                        aria-hidden="true"
                      >
                        {UNAVAILABLE_STATUS_LINE}
                      </Text>
                    )}
                    {matchedAlias && aliasSegments && (
                      <HStack spacing={1.5} mt={1}>
                        <Text
                          fontSize="sm"
                          color={isActive ? "bg-emphasized" : "muted"}
                          flexShrink={0}
                        >
                          {aliasPrefix}
                        </Text>
                        <Text fontSize="sm">
                          {aliasSegments.map(({ text, highlight }, index) => (
                            <Text
                              key={index}
                              color={isActive ? "bg-emphasized" : "fg-subtle"}
                              as="span"
                              fontWeight={highlight ? "bold" : "normal"}
                            >
                              {text}
                            </Text>
                          ))}
                        </Text>
                        {aliasTypeLabel && (
                          <Text
                            fontSize="2xs"
                            fontWeight="medium"
                            letterSpacing="wide"
                            textTransform="uppercase"
                            color={isActive ? "bg-emphasized" : "muted"}
                            bg={isActive ? "whiteAlpha.100" : "bg-subtle"}
                            px={1.5}
                            py={0.5}
                            borderRadius="sm"
                            flexShrink={0}
                          >
                            {aliasTypeLabel}
                          </Text>
                        )}
                      </HStack>
                    )}
                    {!isUnavailable && (
                      <GoLiveDate
                        site={item.sites[0]}
                        color={isActive ? "bg-emphasized" : "warning"}
                      />
                    )}
                  </ListItem>
                );

                if (isUnavailable) {
                  return (
                    <Tooltip
                      key={item.id}
                      label={UNAVAILABLE_TOOLTIP_LABEL}
                      openDelay={400}
                      hasArrow
                      placement="top"
                    >
                      {listItem}
                    </Tooltip>
                  );
                }

                return listItem;
              },
            )
          )}
        </List>
        {pillsOnBottom && <Box mt={3}>{countryPills}</Box>}
        {showHelper && helperText && (
          <FormHelperText mt={4} textAlign="inherit">
            {helperText}
          </FormHelperText>
        )}
        <FacilityRequestModal
          isOpen={isModalOpen}
          onClose={onModalClose}
          query={currValue.inputValue}
          agencyId={agencyIds?.[0]}
          user={user}
        />
      </FormControl>
    </Box>
  );
}
